import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Leaf, Phone, Mail } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { contactInfo } from "../data/products";

import { SEO } from "../components/SEO";

const PROJECT_TYPES = [
  { value: "terrasse", label: "Terrasse en bambou" },
  { value: "parquet", label: "Parquet" },
  { value: "bardage", label: "Bardage" },
];

const DELAIS = [
  { value: "asap", label: "Dès que possible" },
  { value: "3mois", label: "Sous 3 mois" },
  { value: "6mois", label: "Sous 6 mois" },
  { value: "reflexion", label: "En réflexion" },
];

export default function FormulairePartenairePage() {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", commune: "",
    projectTypes: [], surface: "", delai: "", message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const update = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const toggleProjectType = (val) => {
    setFormData((p) => ({
      ...p,
      projectTypes: p.projectTypes.includes(val)
        ? p.projectTypes.filter((v) => v !== val)
        : [...p.projectTypes, val],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error("Veuillez remplir au moins votre nom et téléphone");
      return;
    }
    setIsSubmitting(true);

    const typesText = formData.projectTypes
      .map((v) => PROJECT_TYPES.find((t) => t.value === v)?.label)
      .filter(Boolean).join(", ") || "Non précisé";
    const delaiText = DELAIS.find((d) => d.value === formData.delai)?.label || "Non précisé";

    const lines = [
      `*FORMULAIRE PARTENAIRE — PROBWA*`,
      `*(via Gamm Vert de Saint Paul)*`,
      ``,
      `*Nom :* ${formData.name}`,
      `*Email :* ${formData.email}`,
      `*Tél :* ${formData.phone}`,
      formData.commune ? `*Commune :* ${formData.commune}` : null,
      `*Type de projet :* ${typesText}`,
      formData.surface ? `*Surface estimée :* ${formData.surface} m²` : null,
      `*Délai :* ${delaiText}`,
      ``,
      formData.message ? `*Message :*\n${formData.message}` : null,
    ].filter(Boolean).join("\n");

    // Send email via backend SMTP (OVH)
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiUrl}/api/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: `Formulaire Partenaire — ${formData.name}`,
          source: "Formulaire Partenaire (Gamm Vert)",
          fields: {
            Nom: formData.name,
            Email: formData.email,
            Téléphone: formData.phone,
            Commune: formData.commune || "Non précisé",
            "Type de projet": typesText,
            "Surface estimée": formData.surface ? `${formData.surface} m²` : "Non précisé",
            Délai: delaiText,
            Message: formData.message || "—",
          },
        }),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || "Erreur serveur");
      }
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success("Demande envoyée !", {
        description: "Nous vous répondrons dans les plus brefs délais.",
      });
    } catch (err) {
      setIsSubmitting(false);
      toast.error("Erreur lors de l'envoi", {
        description: "Veuillez réessayer ou nous contacter par téléphone.",
      });
    }
  };

  if (isSubmitted) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#F5F5F0]">
        <Navbar />
        <div className="pt-24 pb-16 px-6 max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-2xl p-12 shadow-sm">
            <div className="w-16 h-16 bg-[#8BC34A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-7 h-7 text-[#8BC34A]" />
            </div>
            <h2 className="text-2xl font-bold text-[#2D2D2D] mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Merci pour votre demande !
            </h2>
            <p className="text-[#8BC34A] font-semibold text-lg mb-3">
              Votre petit geste pour la planète a commencé.
            </p>
            <p className="text-[#666] leading-relaxed">
              Nous avons bien reçu votre formulaire. Notre équipe vous recontactera sous 48h ouvrées
              pour vous proposer une étude personnalisée adaptée à votre projet.
            </p>
          </div>
        </div>
        <Footer />
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#F5F5F0]">
      <Navbar />
      <SEO
        path="/formulaire_partenaire"
        title="Formulaire Partenaire Gamm Vert — Devis Terrasse Bambou"
        description="Demandez votre étude personnalisée gratuite via notre partenaire Gamm Vert de Saint Paul. Terrasses, parquets et bardages en bambou REBO à La Réunion."
      />
      <div className="pt-20 md:pt-24">
        {/* Hero intro */}
        <div className="bg-[#2D2D2D] text-white py-10 md:py-14 px-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Leaf className="w-5 h-5 text-[#8BC34A]" />
              <span className="text-sm text-[#8BC34A] font-medium">Partenaire Gamm Vert de Saint Paul</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Votre projet en bambou commence ici
            </h1>
            <p className="text-sm md:text-base text-[#bbb] leading-relaxed mb-4">
              Suite à votre visite chez notre partenaire Gamm Vert de Saint Paul,
              vous avez découvert nos solutions d'aménagement extérieur en bambou.
              Nous vous remercions pour votre intérêt.
            </p>
            <p className="text-sm md:text-base text-[#bbb] leading-relaxed mb-4">
              PROBWA vous propose une <strong className="text-white">étude personnalisée gratuite</strong>,
              adaptée à votre projet et aux spécificités de votre terrain à La Réunion.
            </p>
            <ul className="text-sm text-[#bbb] space-y-1.5 ml-4">
              <li className="flex items-start gap-2"><span className="text-[#8BC34A] mt-0.5">&#8226;</span> Résister au climat tropical</li>
              <li className="flex items-start gap-2"><span className="text-[#8BC34A] mt-0.5">&#8226;</span> Offrir une finition haut de gamme (sans vis apparentes)</li>
              <li className="flex items-start gap-2"><span className="text-[#8BC34A] mt-0.5">&#8226;</span> Garantir stabilité et durabilité dans le temps</li>
              <li className="flex items-start gap-2"><span className="text-[#8BC34A] mt-0.5">&#8226;</span> Valoriser votre extérieur avec un matériau naturel et élégant</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
          <h2 className="text-xl md:text-2xl font-bold text-[#2D2D2D] mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Demandez votre étude personnalisée
          </h2>
          <p className="text-sm text-[#666] mb-8">
            Merci de compléter le formulaire ci-dessous. Nous vous recontacterons sous 48h ouvrées.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="partner-form">
            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Nom et Prénom *</label>
              <input type="text" value={formData.name} onChange={(e) => update("name", e.target.value)}
                placeholder="Nom et prénom" required
                className="w-full px-4 py-3 border border-[#ddd] rounded-lg focus:outline-none focus:border-[#8BC34A] bg-white text-[#2D2D2D]"
                data-testid="partner-name" />
            </div>
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-1">E-mail</label>
              <input type="email" value={formData.email} onChange={(e) => update("email", e.target.value)}
                placeholder="E-mail"
                className="w-full px-4 py-3 border border-[#ddd] rounded-lg focus:outline-none focus:border-[#8BC34A] bg-white text-[#2D2D2D]"
                data-testid="partner-email" />
            </div>
            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Téléphone *</label>
              <input type="tel" value={formData.phone} onChange={(e) => update("phone", e.target.value)}
                placeholder="06 92 XX XX XX" required
                className="w-full px-4 py-3 border border-[#ddd] rounded-lg focus:outline-none focus:border-[#8BC34A] bg-white text-[#2D2D2D]"
                data-testid="partner-phone" />
            </div>
            {/* Commune */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Commune du projet</label>
              <input type="text" value={formData.commune} onChange={(e) => update("commune", e.target.value)}
                placeholder="Commune"
                className="w-full px-4 py-3 border border-[#ddd] rounded-lg focus:outline-none focus:border-[#8BC34A] bg-white text-[#2D2D2D]"
                data-testid="partner-commune" />
            </div>

            {/* Type de Projet */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Type de Projet</label>
              <div className="flex flex-wrap gap-3">
                {PROJECT_TYPES.map((t) => (
                  <label key={t.value} className="flex items-center gap-2 cursor-pointer" data-testid={`project-type-${t.value}`}>
                    <input type="checkbox" checked={formData.projectTypes.includes(t.value)}
                      onChange={() => toggleProjectType(t.value)}
                      className="w-4 h-4 accent-[#8BC34A]" />
                    <span className="text-sm text-[#2D2D2D]">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Surface */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Surface estimée en m²</label>
              <input type="text" value={formData.surface} onChange={(e) => update("surface", e.target.value)}
                placeholder="Ex: 20"
                className="w-full px-4 py-3 border border-[#ddd] rounded-lg focus:outline-none focus:border-[#8BC34A] bg-white text-[#2D2D2D]"
                data-testid="partner-surface" />
            </div>

            {/* Délais */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-2">Délais souhaités</label>
              <div className="flex flex-wrap gap-3">
                {DELAIS.map((d) => (
                  <label key={d.value} className="flex items-center gap-2 cursor-pointer" data-testid={`delai-${d.value}`}>
                    <input type="radio" name="delai" checked={formData.delai === d.value}
                      onChange={() => update("delai", d.value)}
                      className="w-4 h-4 accent-[#8BC34A]" />
                    <span className="text-sm text-[#2D2D2D]">{d.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-[#2D2D2D] mb-1">Décrivez votre projet</label>
              <textarea value={formData.message} onChange={(e) => update("message", e.target.value)}
                placeholder="Message" rows={4}
                className="w-full px-4 py-3 border border-[#ddd] rounded-lg focus:outline-none focus:border-[#8BC34A] bg-white text-[#2D2D2D] resize-vertical"
                data-testid="partner-message" />
            </div>

            {/* Submit */}
            <button type="submit" disabled={isSubmitting}
              className="w-full py-3.5 bg-[#8BC34A] hover:bg-[#7CB342] text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="partner-submit">
              <Send className="w-4 h-4" />
              {isSubmitting ? "Envoi en cours..." : "Envoyer"}
            </button>

            <p className="text-xs text-[#999] leading-relaxed">
              Les informations recueillies à travers ce formulaire sont destinées à vous envoyer des réponses
              à vos demandes et font l'objet d'un traitement par ProBwa. Pour avoir plus d'informations sur la
              manière dont nous traitons vos données et gérons vos droits, adressez-vous à {contactInfo.email}
            </p>
          </form>

          {/* Contact section */}
          <div className="mt-12 pt-8 border-t border-[#ddd] text-center">
            <h3 className="text-lg font-bold text-[#2D2D2D] mb-6" style={{ fontFamily: "'DM Serif Display', serif" }}>
              OU CONTACTEZ-NOUS
            </h3>
            <div className="flex flex-col sm:flex-row justify-center gap-8">
              <a href={`mailto:${contactInfo.email}`} className="flex flex-col items-center gap-2 text-[#8BC34A] hover:text-[#7CB342] transition-colors">
                <Mail className="w-8 h-8" />
                <span className="font-semibold text-sm">PAR E-MAIL</span>
                <span className="text-xs text-[#666]">{contactInfo.email}</span>
              </a>
              <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} className="flex flex-col items-center gap-2 text-[#8BC34A] hover:text-[#7CB342] transition-colors">
                <Phone className="w-8 h-8" />
                <span className="font-semibold text-sm">PAR TÉLÉPHONE</span>
                <span className="text-xs text-[#666]">{contactInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </motion.div>
  );
}
